import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    clearCampaignReport,
    getCampaignReport,
    getCampaignReportContent,
    getCampaignReportCreators,
    getCampaignReportSyncStatus,
    syncCampaignReport,
} from '../../../redux/buzzbridge/actions';
import {
    campaignReportContentSelector,
    campaignReportCreatorsSelector,
    campaignReportSummarySelector,
    campaignReportSyncStatusSelector,
    gettingCampaignReportSelector,
    syncingCampaignReportSelector,
} from '../../../redux/buzzbridge/selectors';
import type {
    CampaignReportContentSort,
    CampaignReportCreatorSort,
    CampaignReportPlatform,
    CampaignReportWindow,
    ICampaignReportContentItem,
    ICampaignReportCreatorItem,
} from '../../../types/global/campaignReport.types';
import { isCampaignReportBoostable } from '../../../utils/campaignReportBoost';
import type { CampaignReportDataMode } from './CampaignReportDevToolbar';
import {
    buildCampaignReportPreviewContent,
    buildCampaignReportPreviewCreators,
    buildCampaignReportPreviewSummary,
    CAMPAIGN_REPORT_CONTENT_PAGE_SIZE,
    CAMPAIGN_REPORT_CREATORS_PAGE_SIZE,
    getInitialCampaignReportDataMode,
    type CampaignReportPreviewScenario,
} from './campaignReportMockData';

export type CampaignReportTab = 'dashboard' | 'creators';

const UNSUPPORTED_PERMISSIONS_NOTE =
    'Some posts are unsupported. Creators need connected OAuth (Instagram, TikTok, or YouTube) and the live URL must belong to that connected account.';

function paginateClientList<T>(items: T[], page: number, limit: number) {
    const total = items.length;
    const start = (page - 1) * limit;
    return {
        items: items.slice(start, start + limit),
        pagination: {
            page,
            limit,
            total,
            has_more: start + limit < total,
        },
    };
}

export function useCampaignReportPage(campaignId: string | undefined, campaignTitle = 'Campaign') {
    const dispatch = useDispatch();
    const liveSummary = useSelector(campaignReportSummarySelector);
    const liveContent = useSelector(campaignReportContentSelector);
    const liveCreators = useSelector(campaignReportCreatorsSelector);
    const syncStatus = useSelector(campaignReportSyncStatusSelector);
    const loadingSummary = useSelector(gettingCampaignReportSelector);
    const syncing = useSelector(syncingCampaignReportSelector);

    const [reportWindow, setReportWindow] = useState<CampaignReportWindow>('all_time');
    const [tab, setTab] = useState<CampaignReportTab>('dashboard');
    const [platform, setPlatform] = useState<CampaignReportPlatform | null>(null);
    const [contentSort, setContentSort] = useState<CampaignReportContentSort>('views');
    const [creatorSort, setCreatorSort] = useState<CampaignReportCreatorSort>('impact_score');
    const [contentPage, setContentPage] = useState(1);
    const [creatorsPage, setCreatorsPage] = useState(1);
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [boostItem, setBoostItem] = useState<ICampaignReportContentItem | null>(null);
    const [dataMode, setDataMode] = useState<CampaignReportDataMode>(() => getInitialCampaignReportDataMode());
    const [previewScenario, setPreviewScenario] = useState<CampaignReportPreviewScenario>('full');

    const isPreview = dataMode === 'preview';
    const effectiveCampaignId = campaignId ?? 'preview-campaign';

    useEffect(() => {
        const timer = globalThis.setTimeout(() => setSearch(searchInput.trim()), 350);
        return () => globalThis.clearTimeout(timer);
    }, [searchInput]);

    const previewSummary = useMemo(
        () => buildCampaignReportPreviewSummary(effectiveCampaignId, campaignTitle, reportWindow, previewScenario),
        [effectiveCampaignId, campaignTitle, previewScenario, reportWindow],
    );
    const previewContent = useMemo(
        () =>
            buildCampaignReportPreviewContent(
                previewScenario,
                contentPage,
                CAMPAIGN_REPORT_CONTENT_PAGE_SIZE,
                platform,
            ),
        [contentPage, platform, previewScenario],
    );
    const previewCreators = useMemo(
        () => buildCampaignReportPreviewCreators(previewScenario, creatorsPage, CAMPAIGN_REPORT_CREATORS_PAGE_SIZE),
        [creatorsPage, previewScenario],
    );

    const summary = isPreview ? previewSummary : liveSummary;

    const content = useMemo(() => {
        if (isPreview) return previewContent;
        if (liveContent?.pagination) return liveContent;
        const paged = paginateClientList(liveContent?.items ?? [], contentPage, CAMPAIGN_REPORT_CONTENT_PAGE_SIZE);
        return { items: paged.items as ICampaignReportContentItem[], pagination: paged.pagination };
    }, [contentPage, isPreview, liveContent, previewContent]);

    const creators = useMemo(() => {
        if (isPreview) return previewCreators;
        const all = liveCreators?.creators ?? [];
        if (liveCreators?.pagination) return liveCreators;
        const paged = paginateClientList(all, creatorsPage, CAMPAIGN_REPORT_CREATORS_PAGE_SIZE);
        return { creators: paged.items as ICampaignReportCreatorItem[], pagination: paged.pagination };
    }, [creatorsPage, isPreview, liveCreators, previewCreators]);

    const syncPct = summary?.sync?.sync_progress_pct ?? syncStatus?.sync_progress_pct ?? 0;
    const hasPublishedPosts = (summary?.health?.content_published ?? 0) > 0 || (content?.items?.length ?? 0) > 0;

    const contentPagination = content?.pagination;
    const creatorsPagination = creators?.pagination;
    const contentTotalPages = contentPagination
        ? Math.max(1, Math.ceil(contentPagination.total / contentPagination.limit))
        : 1;
    const creatorsTotalPages = creatorsPagination
        ? Math.max(1, Math.ceil(creatorsPagination.total / creatorsPagination.limit))
        : 1;

    const loadContent = useCallback(
        (skipCache = false) => {
            if (!campaignId || isPreview) return;
            dispatch(
                getCampaignReportContent({
                    campaignId,
                    window: reportWindow,
                    platform: platform ?? undefined,
                    search: search || undefined,
                    sort: contentSort,
                    order: 'desc',
                    page: contentPage,
                    limit: CAMPAIGN_REPORT_CONTENT_PAGE_SIZE,
                    skipCache,
                }),
            );
        },
        [campaignId, contentPage, contentSort, dispatch, isPreview, platform, reportWindow, search],
    );

    const loadDashboard = useCallback(
        (skipCache = false) => {
            if (!campaignId || isPreview) return;
            dispatch(getCampaignReport({ campaignId, window: reportWindow, skipCache }));
            loadContent(skipCache);
            dispatch(getCampaignReportSyncStatus({ campaignId, skipCache }));
        },
        [campaignId, dispatch, isPreview, loadContent, reportWindow],
    );

    const loadCreators = useCallback(
        (skipCache = false) => {
            if (!campaignId || isPreview) return;
            dispatch(
                getCampaignReportCreators({
                    campaignId,
                    window: reportWindow,
                    sort: creatorSort,
                    page: creatorsPage,
                    limit: CAMPAIGN_REPORT_CREATORS_PAGE_SIZE,
                    skipCache,
                }),
            );
        },
        [campaignId, creatorSort, creatorsPage, dispatch, isPreview, reportWindow],
    );

    useEffect(() => {
        setContentPage(1);
    }, [reportWindow, platform, contentSort, search, previewScenario, dataMode]);

    useEffect(() => {
        setCreatorsPage(1);
    }, [reportWindow, creatorSort, previewScenario, dataMode]);

    useEffect(() => {
        if (isPreview) return () => {};
        return () => {
            dispatch(clearCampaignReport());
        };
    }, [dispatch, isPreview]);

    useEffect(() => {
        if (!campaignId || isPreview) return;
        loadDashboard();
    }, [campaignId, isPreview, loadDashboard]);

    useEffect(() => {
        if (!campaignId || isPreview) return;
        loadContent();
    }, [campaignId, contentPage, isPreview, loadContent]);

    useEffect(() => {
        if (!campaignId || isPreview) return;
        loadCreators();
    }, [campaignId, creatorSort, creatorsPage, isPreview, loadCreators, reportWindow]);

    useEffect(() => {
        if (!campaignId || isPreview || syncPct >= 100) return;
        const timer = globalThis.setInterval(() => {
            dispatch(getCampaignReportSyncStatus({ campaignId, skipCache: true }));
            dispatch(getCampaignReport({ campaignId, window: reportWindow, skipCache: true }));
            loadContent(true);
        }, 30_000);
        return () => globalThis.clearInterval(timer);
    }, [campaignId, dispatch, isPreview, loadContent, syncPct, reportWindow]);

    const topContent = content?.items ?? [];

    const openBoost = useCallback((item: ICampaignReportContentItem) => {
        if (!isCampaignReportBoostable(item)) return;
        setBoostItem(item);
    }, []);

    const closeBoost = useCallback(() => setBoostItem(null), []);

    const refreshAll = useCallback(() => {
        if (isPreview) return;
        loadDashboard(true);
        loadCreators(true);
    }, [isPreview, loadCreators, loadDashboard]);

    const queueSync = useCallback(() => {
        if (isPreview || !campaignId) return;
        dispatch(syncCampaignReport({ campaignId }));
    }, [campaignId, dispatch, isPreview]);

    const togglePlatform = useCallback((value: CampaignReportPlatform) => {
        setPlatform(prev => (prev === value ? null : value));
    }, []);

    const unsupportedCount = summary?.sync?.by_status?.unsupported ?? syncStatus?.by_status?.unsupported ?? 0;

    const permissionsNote = isPreview
        ? previewScenario === 'syncing'
            ? UNSUPPORTED_PERMISSIONS_NOTE
            : undefined
        : summary?.permissions_note || (unsupportedCount > 0 ? UNSUPPORTED_PERMISSIONS_NOTE : undefined);

    return {
        summary,
        content,
        creators,
        syncStatus,
        loadingSummary: isPreview ? false : loadingSummary,
        syncing: isPreview ? false : syncing,
        reportWindow,
        setReportWindow,
        tab,
        setTab,
        platform,
        togglePlatform,
        contentSort,
        setContentSort,
        creatorSort,
        setCreatorSort,
        contentPage,
        setContentPage,
        creatorsPage,
        setCreatorsPage,
        contentTotalPages,
        creatorsTotalPages,
        searchInput,
        setSearchInput,
        syncPct,
        hasPublishedPosts,
        topContent,
        boostItem,
        openBoost,
        closeBoost,
        refreshAll,
        queueSync,
        dataMode,
        setDataMode,
        previewScenario,
        setPreviewScenario,
        isPreview,
        permissionsNote,
    };
}
